import { Skeleton } from "@gigpro/ui/Skeleton";
import {
  type ForManagerLocationDetail,
  getLocationsRetrieveQueryKey,
  useLocationsJobsSubscriptionTrialCreate,
} from "@sidegigllc/api/client";
import { useQueryClient } from "@tanstack/react-query";
import { useUserRoleForOrganization } from "domains/auth";
import { JobsResubscribeStep } from "domains/hire/components/JobsResubscribeStep";
import { JobsTrialStep } from "domains/hire/components/JobsTrialStep";
import { useJobsGateState } from "domains/hire/hooks/useJobsGateState";
import { useResubscribeJobsPlan } from "domains/hire/hooks/useResubscribeJobsPlan";
import { JOBS_TRIAL_DAYS, jobsGateFor } from "domains/hire/jobsPlan";
import { StripeElementsProvider } from "domains/locations/containers/StripePaymentMethodUpdate/StripeElementsProvider";
import type { PaymentSourceId } from "domains/locations/containers/StripePaymentMethodUpdate/useSaveStripePaymentMethod";
import * as React from "react";
import { toastApiError } from "utils/toastApiError";
import { JobsTrialPaymentStep } from "./JobsTrialPaymentStep";

type JobsTrialStepContainerProps = {
  locationId: number;
  /** What to do once the trial is running (or the plan resubscribed), e.g. re-submit the job that was refused. */
  onStarted: () => Promise<unknown>;
  onBack: () => void;
  submitLabel?: string;
  /** The start button's label while it also saves the inline payment method. */
  saveSubmitLabel?: string;
  purpose?: "workingInterviews" | "unlock" | "settings";
};

/**
 * Whose Stripe customer pays the plan. An org-billed location charges the organization, so only staff and
 * organization admins can add its method here; the location's own setup intent would not clear the gate.
 */
const paymentSourceFor = (
  location: Pick<ForManagerLocationDetail, "id" | "billing_source" | "organization_id">,
  canPayForOrganization: boolean,
): PaymentSourceId | null => {
  if (location.billing_source !== "ORGANIZATION") return { locationId: location.id };
  return canPayForOrganization && location.organization_id ? { organizationId: location.organization_id } : null;
};

export const JobsTrialStepContainer = ({
  locationId,
  onStarted,
  onBack,
  submitLabel = "Start free trial and post job",
  saveSubmitLabel = "Save, start free trial and post job",
  purpose,
}: JobsTrialStepContainerProps) => {
  const queryClient = useQueryClient();
  const { location, status, isStaff, canManage: isAdmin } = useJobsGateState(locationId);
  const organizationRole = useUserRoleForOrganization(location?.organization_id ?? "");
  const startTrial = useLocationsJobsSubscriptionTrialCreate();
  const resubscribe = useResubscribeJobsPlan(locationId);
  const [isStarting, setIsStarting] = React.useState(false);
  // Once the trial is running, a retry after a failed re-post only re-posts: a second trial start would be refused.
  const [trialStarted, setTrialStarted] = React.useState(false);
  // Likewise for a resubscribe, and keeps its step on screen: the refreshed plan no longer offers to resubscribe.
  const [resubscribed, setResubscribed] = React.useState(false);
  // Keeps the inline form mounted after the save: the webhook that clears needs_payment_method lands later.
  const [paymentSaved, setPaymentSaved] = React.useState(false);
  // The server sets the real end date; this is the date the business agrees to, shown before it exists.
  const [trialEndsAt] = React.useState(() => new Date(Date.now() + JOBS_TRIAL_DAYS * 24 * 60 * 60 * 1000));

  if (!location || !status) return <Skeleton className="mt-4 h-64 w-full" />;

  const handleStart = async () => {
    setIsStarting(true);
    try {
      if (!trialStarted) {
        await startTrial.mutateAsync({ locationId });
        setTrialStarted(true);
        await queryClient.invalidateQueries({ queryKey: getLocationsRetrieveQueryKey(locationId) });
      }
      await onStarted();
    } catch (error) {
      toastApiError(error);
    }
    setIsStarting(false);
  };

  // A location that used its trial pays now instead: the same step, with the charge named.
  const handleResubscribe = async () => {
    setIsStarting(true);
    try {
      if (!resubscribed) {
        await resubscribe.mutateAsync(() => setResubscribed(true));
      }
      await onStarted();
    } catch (error) {
      toastApiError(error);
    }
    setIsStarting(false);
  };

  // Staff and team members have no gate of their own, so the step is read from the plan itself.
  if (resubscribed || jobsGateFor(status, { isStaff: false, canManage: true }) === "resubscribe") {
    return (
      <JobsResubscribeStep
        locationId={locationId}
        locationName={location.name}
        status={status}
        isAdmin={isAdmin}
        purpose={purpose}
        resubscribed={resubscribed}
        isStarting={isStarting}
        onStart={handleResubscribe}
        onBack={onBack}
      />
    );
  }

  const paymentSource = paymentSourceFor(location, isStaff || organizationRole === "ADMIN");
  const stepProps = {
    locationId,
    locationName: location.name,
    status,
    isAdmin,
    trialEndsAt,
    purpose,
    isStarting,
    onBack,
  };
  const plainStep = (
    <JobsTrialStep
      {...stepProps}
      billedToOrganization={!paymentSource}
      submitLabel={submitLabel}
      onStart={handleStart}
    />
  );

  const collectsPayment = isAdmin && status.collection === "STRIPE" && (status.needs_payment_method || paymentSaved);
  if (!collectsPayment || !paymentSource) return plainStep;

  return (
    <StripeElementsProvider
      sourceId={paymentSource}
      fallback={<Skeleton className="mt-4 h-64 w-full" />}
      errorFallback={plainStep}
    >
      <JobsTrialPaymentStep
        {...stepProps}
        sourceId={paymentSource}
        submitLabel={saveSubmitLabel}
        paymentSaved={paymentSaved}
        onSaved={() => setPaymentSaved(true)}
        onStart={handleStart}
      />
    </StripeElementsProvider>
  );
};
